import React, { useState, useMemo, useEffect, useRef } from 'react';
import './App.css';
import { mockStalls } from './mock';
import StallCard from './components/StallCard';
import StallListItem from './components/StallListItem';
import StallModal from './components/StallModal';
import MenuDrawer from './components/MenuDrawer';
import VendorDashboard from './components/VendorDashboard';
import AdminDashboard from './components/AdminDashboard';
import ResetPassword from './components/ResetPassword';
import CookieConsent from './components/CookieConsent';
import { Store, Menu, Search, X, ChevronLeft, ChevronRight, ArrowUp, MapPin, ChevronDown, LayoutGrid, Tent, PartyPopper } from 'lucide-react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { getFirebaseApp } from './lib/firebase';
import { trackAppOpened, trackScreenView, trackStallViewed, trackSearch, trackFilterApplied, trackError } from './lib/analytics';
import { toast, Toaster } from 'sonner';
import MapExplore from './components/MapExplore';
import ProvincePillDropdown from './components/ProvincePillDropdown';
import PullToRefresh from './components/PullToRefresh';
import useOverflowFade from './hooks/useOverflowFade';
import { fetchWithRetry } from './lib/fetchWithRetry';

// Initialize Firebase on app load
getFirebaseApp();

function AppContent() {
  const { isAdmin, isVendor, isAuthenticated, refreshUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedMarket, setSelectedMarket] = useState('All Markets');
  const [stallNumberFilter, setStallNumberFilter] = useState('');
  const [tags, setTags] = useState('');
  const [selectedStall, setSelectedStall] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState('random');
  const [shuffleSeed] = useState(() => Math.random());
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Default to grid-4 on desktop/tablet, grid-2 on mobile
  const [viewLayout, setViewLayout] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768 ? 'grid-2' : 'grid-4';
    }
    return 'grid-4';
  });
  const [theme, setTheme] = useState('light');
  const [showVendorDashboard, setShowVendorDashboard] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [showMarketsDropdown, setShowMarketsDropdown] = useState(false);
  const [categories, setCategories] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [expos, setExpos] = useState([]);
  const [festivals, setFestivals] = useState([]);
  // Province → { markets, expos, festivals } lookup table (built from /api/categories/provinces)
  const [provinceItems, setProvinceItems] = useState({});
  const [selectedProvince, setSelectedProvince] = useState('all');
  const [exploreTab, setExploreTab] = useState('all');
  const [mapDirections, setMapDirections] = useState(null);

  // When directions is requested, switch to map view
  const handleDirectionsRequest = (stall, lat, lng, market) => {
    setMapDirections({ stallName: stall.stallName, lat, lng, market });
    setExploreTab('map');
  };

  // Shuffled combined list for "All" tab - reshuffles on data change
  const shuffledAllPills = useMemo(() => {
    const combined = [...markets, ...expos, ...festivals];
    for (let i = combined.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [combined[i], combined[j]] = [combined[j], combined[i]];
    }
    return combined;
  }, [markets, expos, festivals]);
  const [stalls, setStalls] = useState([]); // NO MOCK DATA - REAL DATA ONLY
  const [loading, setLoading] = useState(true);
  const [resetPasswordToken, setResetPasswordToken] = useState(null);
  const [stallsPerPage, setStallsPerPage] = useState('20');
  const [currentPage, setCurrentPage] = useState(1);
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  const categoryScrollRef = useRef(null);
  const stallGridRef = useRef(null);
  const subFilterFade = useOverflowFade([exploreTab, markets, expos, festivals], { leftFade: false });
  const API_URL = window.location.origin;
  const categoryRefs = useRef({});

  // Update view layout on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setViewLayout('grid-2');
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Show/hide scroll-to-top button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 200);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // PWA shortcut / deep-link support: ?view=map opens map view on launch
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const view = urlParams.get('view');
    if (view === 'map') {
      setExploreTab('map');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (view === 'explore') {
      setExploreTab('all');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // PWA file_handlers: when the OS opens an image with WAYO, launchQueue
  // fires; also supports `?open=vendor-upload` as a fallback deep-link.
  useEffect(() => {
    const openVendor = () => setShowVendorDashboard(true);

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('open') === 'vendor-upload') {
      openVendor();
      window.history.replaceState({}, '', window.location.pathname);
    }

    if ('launchQueue' in window && 'files' in window.LaunchParams.prototype) {
      window.launchQueue.setConsumer((launchParams) => {
        if (launchParams.files && launchParams.files.length > 0) {
          openVendor();
          toast.info('Open your vendor dashboard to upload the selected image.');
        }
      });
    }
  }, []);

  // Check for reset password token in URL - must be before any early returns
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const path = window.location.pathname;
    
    if (path === '/reset-password' && token) {
      setResetPasswordToken(token);
    }
  }, []);

  // Handle ?stall= URL parameter to open a specific stall modal (for share links)
  useEffect(() => {
    if (stalls.length === 0) return; // Wait for stalls to load
    
    const urlParams = new URLSearchParams(window.location.search);
    const stallId = urlParams.get('stall');
    
    if (stallId) {
      const stall = stalls.find(s => s.id === stallId || s._id === stallId);
      if (stall) {
        setSelectedStall(stall);
        setIsModalOpen(true);
        // Clear the URL parameter
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [stalls]);

  // Handle payment success/cancel URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const plan = urlParams.get('plan');
    
    if (paymentStatus === 'success') {
      toast.success(`Payment successful! Your ${plan || ''} subscription is now active.`);
      // Clear the URL parameters
      window.history.replaceState({}, '', window.location.pathname);
      
      // Refresh user data to get updated subscription status
      // Then open the vendor dashboard after a short delay
      const handlePaymentSuccess = async () => {
        // Wait for ITN to process (give PayFast a moment to send the notification)
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Refresh user data from server
        if (refreshUser) {
          await refreshUser();
        }
        
        // Open the vendor dashboard
        if (isVendor || isAdmin) {
          setShowVendorDashboard(true);
        }
      };
      
      handlePaymentSuccess();
    } else if (paymentStatus === 'cancelled') {
      toast.error('Payment was cancelled. You can try again from your dashboard.');
      // Clear the URL parameters
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [isVendor, isAdmin, refreshUser]);

  // Track app opened on mount
  useEffect(() => {
    trackAppOpened();
    trackScreenView('Home', 'MainPage');
  }, []);

  // Fetch categories, markets and stalls from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetchWithRetry(`${API_URL}/api/categories/`);
        if (response.ok) {
          const data = await response.json();
          
          setCategories(data);
          // Cache in localStorage
          localStorage.setItem('wayo_categories', JSON.stringify(data));
        } else {
          // Load from cache if API fails
          const cached = localStorage.getItem('wayo_categories');
          if (cached) {
            setCategories(JSON.parse(cached));
          }
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Load from cache if API fails
        const cached = localStorage.getItem('wayo_categories');
        if (cached) {
          setCategories(JSON.parse(cached));
        }
      }
    };

    const fetchMarkets = async () => {
      try {
        const response = await fetchWithRetry(`${API_URL}/api/categories/markets`);
        if (response.ok) {
          const data = await response.json();
          setMarkets(data);
          localStorage.setItem('wayo_markets', JSON.stringify(data));
        } else {
          const cached = localStorage.getItem('wayo_markets');
          if (cached) setMarkets(JSON.parse(cached));
        }
      } catch (error) {
        const cached = localStorage.getItem('wayo_markets');
        if (cached) setMarkets(JSON.parse(cached));
      }
    };

    const fetchExposAndFestivals = async () => {
      try {
        const [expoRes, festRes, provRes] = await Promise.all([
          fetchWithRetry(`${API_URL}/api/categories/expos`),
          fetchWithRetry(`${API_URL}/api/categories/festivals`),
          fetchWithRetry(`${API_URL}/api/categories/provinces`),
        ]);
        if (expoRes.ok) {
          const d = await expoRes.json();
          setExpos(d);
          localStorage.setItem('wayo_expos', JSON.stringify(d));
        } else {
          const cached = localStorage.getItem('wayo_expos');
          if (cached) setExpos(JSON.parse(cached));
        }
        if (festRes.ok) {
          const d = await festRes.json();
          setFestivals(d);
          localStorage.setItem('wayo_festivals', JSON.stringify(d));
        } else {
          const cached = localStorage.getItem('wayo_festivals');
          if (cached) setFestivals(JSON.parse(cached));
        }
        if (provRes.ok) {
          const d = await provRes.json();
          setProvinceItems(d.items || {});
          localStorage.setItem('wayo_provinces', JSON.stringify(d.items || {}));
        } else {
          const cached = localStorage.getItem('wayo_provinces');
          if (cached) setProvinceItems(JSON.parse(cached));
        }
      } catch (error) {
        console.error('Error fetching expos/festivals/provinces:', error);
        const cachedE = localStorage.getItem('wayo_expos');
        if (cachedE) setExpos(JSON.parse(cachedE));
        const cachedF = localStorage.getItem('wayo_festivals');
        if (cachedF) setFestivals(JSON.parse(cachedF));
        const cachedP = localStorage.getItem('wayo_provinces');
        if (cachedP) setProvinceItems(JSON.parse(cachedP));
      }
    };

    const fetchStalls = async () => {
      setLoading(true);
      // Show cached stalls immediately for a zero-flash experience
      const cached = localStorage.getItem('wayo_stalls');
      if (cached) {
        try { setStalls(JSON.parse(cached)); } catch (_) {}
      }
      try {
        const response = await fetchWithRetry(`${API_URL}/api/stalls/`, {}, { retries: 4, backoffMs: 500 });
        if (response.ok) {
          const data = await response.json();
          // Map backend field names to match frontend expectations
          const mappedData = data.map(stall => ({
            ...stall,
            id: stall._id, // Map _id to id
            location: stall.location || { lat: null, lng: null, address: null }
          }));
          setStalls(mappedData);
          localStorage.setItem('wayo_stalls', JSON.stringify(mappedData));
        } else {
          console.error('Failed to fetch stalls; keeping cache');
        }
      } catch (error) {
        console.error('Error fetching stalls:', error);
        // Keep whatever cache we already loaded above
      } finally {
        setLoading(false);
      }
    };
    
    fetchCategories();
    fetchMarkets();
    fetchExposAndFestivals();
    fetchStalls();

    // Listen for category/tag updates from admin dashboard
    const handleCategoryUpdate = () => {
      
      fetchCategories();
      fetchStalls(); // Also refetch stalls as they may have new categories
    };

    window.addEventListener('wayo-categories-updated', handleCategoryUpdate);
    
    return () => {
      window.removeEventListener('wayo-categories-updated', handleCategoryUpdate);
    };
  }, [API_URL]);

  // Refetch categories when admin dashboard closes
  useEffect(() => {
    // Refetch whenever admin dashboard state changes
    const refetchData = async () => {
      
      try {
        const response = await fetch(`${API_URL}/api/categories/`);
        if (response.ok) {
          const data = await response.json();
          
          setCategories(data);
          localStorage.setItem('wayo_categories', JSON.stringify(data));
        }
      } catch (error) {
        console.error('Error refetching categories:', error);
      }
    };
    
    if (!showAdminDashboard) {
      // When admin dashboard closes, refetch after a short delay to ensure backend is updated
      setTimeout(() => refetchData(), 500);
    }
  }, [showAdminDashboard, API_URL]);

  const handleDashboardClick = () => {
    if (isAdmin) {
      setShowAdminDashboard(true);
    } else if (isVendor) {
      setShowVendorDashboard(true);
    }
  };

  // Apply theme to document
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // Center selected category
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    // Track category filter
    if (category !== 'All Categories') {
      trackFilterApplied('category', category);
    }
    
    // Wait for next tick to ensure refs are updated
    setTimeout(() => {
      const categoryButton = categoryRefs.current[category];
      const scrollContainer = categoryScrollRef.current;
      
      if (categoryButton && scrollContainer) {
        const buttonLeft = categoryButton.offsetLeft;
        const buttonWidth = categoryButton.offsetWidth;
        const containerWidth = scrollContainer.offsetWidth;
        
        const scrollPosition = buttonLeft - (containerWidth / 2) + (buttonWidth / 2);
        
        scrollContainer.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }, 50);
  };

  // Track market filter changes
  const handleMarketChange = (market) => {
    setSelectedMarket(market);
    if (market !== 'All Markets') {
      trackFilterApplied('market', market);
    }
  };

  // Track tag filter changes
  const handleTagsChange = (tagValue) => {
    setTags(tagValue);
    if (tagValue) {
      trackFilterApplied('tag', tagValue);
    }
  };

  // Province-aware lists of markets / expos / festivals
  // When a province is selected, only show items assigned to that province.
  // When "all" is selected, show every item.
  const provinceMarkets = useMemo(() => {
    if (selectedProvince === 'all') return markets;
    const allowed = new Set(provinceItems?.[selectedProvince]?.markets || []);
    return markets.filter((m) => allowed.has(m));
  }, [markets, provinceItems, selectedProvince]);

  const provinceExpos = useMemo(() => {
    if (selectedProvince === 'all') return expos;
    const allowed = new Set(provinceItems?.[selectedProvince]?.expos || []);
    return expos.filter((e) => allowed.has(e));
  }, [expos, provinceItems, selectedProvince]);

  const provinceFestivals = useMemo(() => {
    if (selectedProvince === 'all') return festivals;
    const allowed = new Set(provinceItems?.[selectedProvince]?.festivals || []);
    return festivals.filter((f) => allowed.has(f));
  }, [festivals, provinceItems, selectedProvince]);

  // Set of all market/expo/festival names that belong to the active province
  const provinceAllowedLocations = useMemo(() => {
    if (selectedProvince === 'all') return null; // null = no restriction
    const p = provinceItems?.[selectedProvince] || {};
    return new Set([...(p.markets || []), ...(p.expos || []), ...(p.festivals || [])]);
  }, [provinceItems, selectedProvince]);

  const filteredStalls = useMemo(() => {
    let filtered = stalls.filter((stall) => {
      // Search query matches stall name, category, stall number, OR tags
      const stallTags = stall.tags || [];
      const matchesSearch = 
        stall.stallName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        stall.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        stall.stallNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        stallTags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'All Categories' || stall.category === selectedCategory;

      // Province filter: stall must have at least one location in the selected province
      let matchesProvince = true;
      if (provinceAllowedLocations) {
        const stallLocs = [
          stall.market,
          ...((stall.locations || []).map((loc) => loc.market)),
        ].filter(Boolean);
        matchesProvince = stallLocs.some((loc) => provinceAllowedLocations.has(loc));
      }
      if (!matchesProvince) return false;
      
      // Check both primary market AND locations array for multi-market support
      let matchesMarket = selectedMarket === 'All Markets';
      if (!matchesMarket) {
        // Check primary market field
        if (stall.market === selectedMarket) {
          matchesMarket = true;
        }
        // Check locations array for multi-market stalls
        if (!matchesMarket && stall.locations && Array.isArray(stall.locations)) {
          matchesMarket = stall.locations.some(loc => loc.market === selectedMarket);
        }
      }

      // When an explore tab is active (not "all"), restrict to that location type
      if (matchesMarket && exploreTab !== 'all') {
        const locationPool = exploreTab === 'markets' ? markets
          : exploreTab === 'expos' ? expos
          : exploreTab === 'festivals' ? festivals
          : [];
        if (locationPool.length > 0) {
          const stallLocations = [
            stall.market,
            ...((stall.locations || []).map(loc => loc.market))
          ];
          matchesMarket = stallLocations.some(loc => locationPool.includes(loc));
        }
      }
      
      // Tag filter - check if selected tag exists in stall's tags array
      const matchesTags = 
        !tags || 
        stallTags.some(tag => tag.toLowerCase() === tags.toLowerCase());
      
      return matchesSearch && matchesCategory && matchesMarket && matchesTags;
    });

    // Sort the filtered results
    let sorted;
    if (sortBy === 'random') {
      // Fisher-Yates shuffle for true random order on each load
      sorted = [...filtered];
      for (let i = sorted.length - 1; i > 0; i--) {
        const j = Math.floor((shuffleSeed * 1000 + i) % (i + 1));
        [sorted[i], sorted[j]] = [sorted[j], sorted[i]];
      }
    } else {
      sorted = [...filtered].sort((a, b) => {
        switch (sortBy) {
          case 'name-asc':
            return a.stallName.localeCompare(b.stallName);
          case 'name-desc':
            return b.stallName.localeCompare(a.stallName);
          case 'stall-asc':
            return a.stallNumber.localeCompare(b.stallNumber);
          case 'stall-desc':
            return b.stallNumber.localeCompare(a.stallNumber);
          case 'category-asc':
            return a.category.localeCompare(b.category);
          case 'category-desc':
            return b.category.localeCompare(a.category);
          default:
            return 0;
        }
      });
    }

    return sorted;
  }, [stalls, searchQuery, selectedCategory, selectedMarket, exploreTab, markets, expos, festivals, stallNumberFilter, tags, sortBy, shuffleSeed, provinceAllowedLocations]);

  // Pagination logic
  const paginatedStalls = useMemo(() => {
    if (stallsPerPage === 'all') {
      return filteredStalls;
    }
    const perPage = parseInt(stallsPerPage);
    const startIndex = (currentPage - 1) * perPage;
    return filteredStalls.slice(startIndex, startIndex + perPage);
  }, [filteredStalls, stallsPerPage, currentPage]);

  const totalPages = useMemo(() => {
    if (stallsPerPage === 'all') return 1;
    return Math.ceil(filteredStalls.length / parseInt(stallsPerPage));
  }, [filteredStalls.length, stallsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, selectedMarket, tags, sortBy]);

  // Debounced search keyword logging (2 second pause)
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (!trimmed || trimmed.length < 2) return;

    const timer = setTimeout(() => {
      fetch(`${API_URL}/api/analytics/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ search_term: trimmed, results_count: filteredStalls.length })
      }).catch(() => {});
    }, 2000);

    return () => clearTimeout(timer);
  }, [searchQuery, filteredStalls.length]);

  // Handle page change with smooth scroll to the top of the STALL GRID —
  // not the top of the page — so the header/filters stay in view and it
  // feels like a continuation, not a fresh load.
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Wait one frame so the new page renders, then scroll the stall grid
    // into view with a small offset to clear any sticky header.
    requestAnimationFrame(() => {
      const el = stallGridRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const HEADER_OFFSET = 96; // approx sticky-header height on desktop/mobile
      const target = window.scrollY + rect.top - HEADER_OFFSET;
      window.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
    });
  };

  const handleStallClick = (stall) => {
    setSelectedStall(stall);
    setIsModalOpen(true);
    // Track stall viewed with full details
    trackStallViewed(
      stall._id || stall.id, 
      stall.stallName, 
      stall.category, 
      stall.market || null, 
      stall.tags || []
    );
  };

  const getGridClassName = () => {
    switch (viewLayout) {
      case 'grid-1':
        return 'grid grid-cols-1 gap-4 sm:gap-6 pb-12 w-full';
      case 'grid-2':
        return 'grid grid-cols-2 gap-2 sm:gap-4 md:gap-6 pb-12 w-full';
      case 'grid-4':
        return 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 pb-12 w-full';
      case 'list':
        return 'flex flex-col gap-4 sm:gap-6 pb-12 w-full';
      default:
        return 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 pb-12 w-full';
    }
  };

  // Handle back from reset password page
  const handleBackFromResetPassword = (openLogin = false) => {
    setResetPasswordToken(null);
    // Clear the URL
    window.history.pushState({}, '', '/');
    
    // If requested, open login modal via URL parameter
    if (openLogin) {
      window.history.pushState({}, '', '/?login=true');
      // Trigger menu drawer to handle the login parameter
      setIsMenuOpen(true);
    }
  };

  // If we're on the reset password page, show that instead
  if (resetPasswordToken) {
    return <ResetPassword token={resetPasswordToken} onBackToHome={handleBackFromResetPassword} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0f0f10]">
      {/* Header — consistent height and styling across mobile, tablet and desktop */}
      <header className="bg-white dark:bg-[#1a1a1a] shadow-md sticky top-0 z-50 border-b-4 border-[#FFD12D]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between gap-2 sm:gap-4 md:gap-6 h-14">
            {/* Logo — combined horizontal logo image; swaps between black-text (light) and yellow-text (dark) */}
            <div className="flex items-center flex-shrink-0 h-full">
              <img
                src="/logos/wayo-logo-black-hdr.webp"
                alt="WAYO — The Vendor Platform"
                width="383"
                height="132"
                decoding="async"
                fetchpriority="high"
                className="block dark:hidden h-10 sm:h-11 w-auto object-contain"
              />
              <img
                src="/logos/wayo-logo-yellow-hdr.webp"
                alt="WAYO — The Vendor Platform"
                width="383"
                height="132"
                decoding="async"
                fetchpriority="high"
                className="hidden dark:block h-10 sm:h-11 w-auto object-contain"
              />
            </div>

            {/* Search Bar — flexes to fill all available space between logo and menu.
                Logo and menu button stay a fixed size on every breakpoint. */}
            <div className="flex-1 min-w-0 max-w-2xl h-full">
              <div className="w-full h-full relative bg-[#FFD12D] rounded-full p-[4px] shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="relative h-full flex items-center">
                  <Input
                    type="text"
                    placeholder="Search WAYO..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="search-input"
                    className="w-full h-full pl-4 sm:pl-5 pr-12 sm:pr-14 text-base bg-white dark:bg-[#1a1a1a] dark:text-white border-0 rounded-full focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-gray-400"
                    style={{ fontSize: '16px' }}
                  />
                  {searchQuery ? (
                    <button
                      onClick={() => setSearchQuery('')}
                      data-testid="search-clear-btn"
                      className="absolute right-1 top-1/2 transform -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-[#FFD12D] hover:bg-[#252525] rounded-full transition-all duration-300 group"
                    >
                      <X className="w-5 h-5 text-[#252525] group-hover:text-[#FFD12D]" />
                    </button>
                  ) : (
                    <div className="absolute right-1 top-1/2 transform -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-[#FFD12D] rounded-full">
                      <Search className="w-5 h-5 text-[#252525]" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Menu Button — proper pill shape on every breakpoint (rounded-full, yellow border).
                Mobile: fixed-width circle (icon only). Desktop: pill with icon + "Menu" text. Same height as search bar. */}
            <button
              type="button"
              onClick={() => setIsMenuOpen(true)}
              data-testid="header-menu-btn"
              aria-label="Menu"
              className="flex-shrink-0 inline-flex items-center justify-center gap-2 h-full aspect-square sm:aspect-auto sm:px-5 rounded-full border-2 border-[#FFD12D] bg-white dark:bg-transparent text-[#252525] dark:text-white hover:bg-[#FFD12D] hover:text-[#252525] font-semibold transition-all duration-300 transform hover:scale-105"
            >
              <Menu className="w-5 h-5" />
              <span className="hidden sm:inline text-base">Menu</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={`max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-4 ${exploreTab === 'map' ? 'pb-2' : 'pb-4'}`}>
        {/* Explore/Map Toggle - pill style */}
        <div className="flex justify-center mb-4">
          <div
            className="relative flex items-center bg-gray-200 dark:bg-gray-700 rounded-full p-1 w-[220px] h-[44px] cursor-pointer"
            onClick={() => setExploreTab(exploreTab === 'map' ? 'all' : 'map')}
            data-testid="explore-map-toggle"
          >
            <div
              className={`absolute top-1 left-1 h-[36px] w-[106px] bg-[#FFD12D] rounded-full transition-all duration-300 ease-in-out shadow-sm ${
                exploreTab === 'map' ? 'translate-x-[104px]' : 'translate-x-0'
              }`}
            />
            <span className={`relative z-10 flex-1 text-center text-base font-bold leading-[44px] transition-colors duration-300 ${
              exploreTab !== 'map' ? 'text-[#252525]' : 'text-gray-500 dark:text-gray-400'
            }`}>
              Explore
            </span>
            <span className={`relative z-10 flex-1 text-center text-base font-bold leading-[44px] transition-colors duration-300 ${
              exploreTab === 'map' ? 'text-[#252525]' : 'text-gray-500 dark:text-gray-400'
            }`}>
              Map
            </span>
          </div>
        </div>

        {/* Explore Tabs: Markets, Expos, Festivals — hidden in map mode */}
        {exploreTab !== 'map' && (
        <div className="overflow-x-auto no-scrollbar mb-4">
          <div className="flex items-center justify-center gap-1.5 px-4 w-max mx-auto bg-gray-100 dark:bg-gray-800 rounded-full p-1.5">
          {[
            { key: 'all', label: 'All', icon: LayoutGrid },
            { key: 'markets', label: 'Markets', icon: MapPin },
            { key: 'expos', label: 'Expos', icon: Tent },
            { key: 'festivals', label: 'Festivals', icon: PartyPopper },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => {
                setExploreTab(key);
                if (key === 'all') handleMarketChange('All Markets');
                setShowMarketsDropdown(false);
              }}
              className={`flex items-center justify-center gap-1 px-3 py-1.5 rounded-full font-bold text-xs transition-all duration-300 flex-shrink-0 ${
                exploreTab === key
                  ? 'bg-[#FFD12D] text-[#252525]'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
          </div>
        </div>
        )}


        {/* Map View */}
        {exploreTab === 'map' ? (
          <div className="mt-2 mb-2 px-2">
            <MapExplore
              stalls={stalls}
              selectedMarket={selectedMarket}
              searchQuery={searchQuery}
              onStallClick={(stall) => { setExploreTab('all'); handleStallClick(stall); }}
              directionsTarget={mapDirections}
              onClearDirections={() => setMapDirections(null)}
              expoNames={expos}
              festivalNames={festivals}
              marketNames={markets}
              provinceItems={provinceItems}
              fullHeight
            />
          </div>
        ) : (
        <>
        {/* Second level: combined province dropdown + location names */}
        {exploreTab !== 'all' && (
          <div className="mb-4 flex justify-center px-3 sm:px-4">
            <div
              ref={subFilterFade.ref}
              className="flex items-center gap-1.5 bg-white/90 dark:bg-[#252525]/90 backdrop-blur-sm rounded-full pl-0 pr-2 py-1.5 shadow-md overflow-x-auto no-scrollbar max-w-full"
              data-testid="explore-sub-filter-pills"
              style={{
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-x',
                overscrollBehaviorX: 'contain',
                ...(subFilterFade.maskStyle || {}),
              }}
            >
              <div className="sticky left-0 z-[5] flex-shrink-0 bg-white dark:bg-[#252525] -my-1.5 py-1.5 pl-3 pr-3 mr-1 rounded-l-full shadow-[6px_0_8px_-6px_rgba(0,0,0,0.15)]">
                <ProvincePillDropdown
                  value={selectedProvince}
                  onChange={(p) => { setSelectedProvince(p); setSelectedMarket('All Markets'); }}
                  testId="explore-province-dd"
                />
              </div>
              {(exploreTab === 'markets' ? provinceMarkets : exploreTab === 'expos' ? provinceExpos : provinceFestivals).map(name => (
                <button
                  key={name}
                  onClick={() => handleMarketChange(name)}
                  data-testid={`explore-sub-filter-${name}`}
                  className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-bold transition-all whitespace-nowrap ${
                    selectedMarket === name
                      ? 'bg-[#FFD12D] text-[#252525]'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results Count - Only show when searching */}
        {searchQuery && (
          <div className="mb-8 mt-6">
            <p className="text-gray-700 dark:text-gray-300 font-semibold">
              {filteredStalls.length} {filteredStalls.length === 1 ? 'stall' : 'stalls'} found
            </p>
          </div>
        )}

        {/* Pagination Info */}
        <div ref={stallGridRef}>
          {stallsPerPage !== 'all' && filteredStalls.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Showing {((currentPage - 1) * parseInt(stallsPerPage)) + 1} - {Math.min(currentPage * parseInt(stallsPerPage), filteredStalls.length)} of {filteredStalls.length} stalls
              </p>
              {totalPages > 1 && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Page {currentPage} of {totalPages}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Stalls Grid or List */}
        {loading && stalls.length === 0 ? (
          // Skeleton cards match the real card's exact height (h-32 sm:h-40
          // md:h-48 banner + text block) so the grid area is reserved and
          // CLS stays near zero while the fetch is in flight.
          <div className={getGridClassName()}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={`skeleton-${i}`} className="overflow-hidden bg-white dark:bg-[#1a1a1a] border-2 border-gray-100 dark:border-gray-800 rounded-lg w-full animate-pulse" data-testid={`stall-skeleton-${i}`}>
                <div className="h-32 sm:h-40 md:h-48 bg-gray-200 dark:bg-gray-800" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-1/2" />
                  <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded-full w-full mt-2" />
                </div>
              </div>
            ))}
          </div>
        ) : paginatedStalls.length > 0 ? (
          <div className={getGridClassName()}>
            {paginatedStalls.map((stall, idx) => (
              viewLayout === 'list' ? (
                <StallListItem 
                  key={stall.id} 
                  stall={stall} 
                  onClick={() => handleStallClick(stall)}
                  selectedMarket={selectedMarket}
                  onGetDirections={(s, lat, lng, market) => handleDirectionsRequest(s, lat, lng, market)}
                />
              ) : (
                <StallCard 
                  key={stall.id} 
                  stall={stall} 
                  onClick={() => handleStallClick(stall)}
                  selectedMarket={selectedMarket}
                  onGetDirections={(s, lat, lng, market) => handleDirectionsRequest(s, lat, lng, market)}
                  eager={idx < 4}
                />
              )
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-24 h-24 bg-gray-200 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Store className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">No stalls found</h3>
            <p className="text-gray-600 dark:text-gray-400">Try adjusting your search or filters</p>
          </div>
        )}

        {/* Pagination Controls */}
        {stallsPerPage !== 'all' && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8 pb-8">
            <Button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              variant="outline"
              size="sm"
              className="hidden sm:flex border-[#FFD12D] text-[#252525] dark:text-white hover:bg-[#FFD12D] disabled:opacity-50"
            >
              First
            </Button>
            <Button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              variant="outline"
              size="sm"
              className="border-[#FFD12D] text-[#252525] dark:text-white hover:bg-[#FFD12D] disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Previous</span>
            </Button>
            
            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    className={currentPage === pageNum 
                      ? "bg-[#FFD12D] text-[#252525] hover:bg-[#252525] hover:text-[#FFD12D]" 
                      : "border-gray-300 dark:border-gray-600 text-[#252525] dark:text-white hover:bg-[#FFD12D] hover:border-[#FFD12D]"
                    }
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>

            <Button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              variant="outline"
              size="sm"
              className="border-[#FFD12D] text-[#252525] dark:text-white hover:bg-[#FFD12D] disabled:opacity-50"
            >
              <span className="hidden sm:inline mr-1">Next</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              variant="outline"
              size="sm"
              className="hidden sm:flex border-[#FFD12D] text-[#252525] dark:text-white hover:bg-[#FFD12D] disabled:opacity-50"
            >
              Last
            </Button>
          </div>
        )}
        </>
        )}
      </main>

      {/* Stall Detail Modal */}
      <StallModal 
        stall={selectedStall} 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        filterMarket={selectedMarket}
        onGetDirections={(stall, lat, lng, market) => handleDirectionsRequest(stall, lat, lng, market)}
      />

      {/* Menu Drawer */}
      <MenuDrawer
        isOpen={isMenuOpen}
        onOpenChange={setIsMenuOpen}
        category={selectedCategory}
        onCategoryChange={handleCategoryChange}
        market={selectedMarket}
        onMarketChange={handleMarketChange}
        markets={provinceMarkets}
        expos={provinceExpos}
        festivals={provinceFestivals}
        province={selectedProvince}
        onProvinceChange={(p) => { setSelectedProvince(p); setSelectedMarket('All Markets'); }}
        exploreTab={exploreTab}
        onExploreTabChange={setExploreTab}
        tags={tags}
        onTagsChange={handleTagsChange}
        categories={categories}
        sortBy={sortBy}
        onSortChange={setSortBy}
        viewLayout={viewLayout}
        onViewChange={setViewLayout}
        stallsPerPage={stallsPerPage}
        onStallsPerPageChange={(value) => {
          setStallsPerPage(value);
          setCurrentPage(1);
        }}
        theme={theme}
        onThemeChange={setTheme}
        onDashboardClick={handleDashboardClick}
        onOpenVendorDashboard={() => setShowVendorDashboard(true)}
        onOpenAdminDashboard={() => setShowAdminDashboard(true)}
      />

      {isVendor && (
        <VendorDashboard
          isOpen={showVendorDashboard}
          onClose={() => setShowVendorDashboard(false)}
        />
      )}

      {isAdmin && (
        <AdminDashboard
          isOpen={showAdminDashboard}
          onClose={() => setShowAdminDashboard(false)}
        />
      )}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <Button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] shadow-lg transition-all duration-300 transform hover:scale-110 flex items-center justify-center"
          data-testid="scroll-to-top-btn"
        >
          <ArrowUp className="w-6 h-6" />
        </Button>
      )}

      {/* Cookie Consent Banner */}
      <CookieConsent />

      {/* Global Footer */}
      <footer className="mt-4 py-3 px-4 border-t border-gray-200 dark:border-gray-800 text-center" data-testid="app-footer">
        <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400">
          Copyright © Botties Group (Pty) Ltd {new Date().getFullYear()}
        </p>
      </footer>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Toaster 
        position="top-center" 
        richColors 
        toastOptions={{
          duration: 4000,
          style: {
            background: '#252525',
            color: '#fff',
            border: '2px solid #FFD12D',
          },
        }}
      />
      <AppContent />
    </AuthProvider>
  );
}

// Wrap in PullToRefresh at the root so it can hook into touch events
// only when the app is running as an installed PWA (standalone).
const AppWithPTR = () => (
  <PullToRefresh>
    <App />
  </PullToRefresh>
);

export default AppWithPTR;